import React, { useEffect, useState } from 'react';
import { listCandidateApplications } from '../../api/candidatePortal';
import { getApplicationStatusChip } from '../../utils/formatters';
import VerdictBadge from '../components/VerdictBadge';
import FeedbackBody from '../components/FeedbackBody';

export default function CandidateApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    listCandidateApplications()
      .then((res) => {
        // Real data only: no prototype applications, no invented company names.
        const cleaned = Array.isArray(res.data) ? res.data : [];

        setApps(cleaned);
      })
      .catch((err) => {
        console.warn('[Applications]', err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  const getInitials = (orgName) => {
    if (!orgName) return 'SA';
    const words = orgName.trim().split(/\s+/);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return orgName.slice(0, 2).toUpperCase();
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div>
        <h1
          className="text-2xl sm:text-[26px] font-semibold text-[var(--navy-950)] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          My Applications
        </h1>
        <p className="text-[13.5px] text-[var(--ink-soft)] mt-1">
          Track where you stand in each hiring process.
        </p>
      </div>

      {loading ? (
        <div className="py-24 text-center">
          <div className="w-8 h-8 border-2 border-[var(--teal-500)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[var(--ink-soft)]">Loading applications…</p>
        </div>
      ) : !apps.length ? (
        <div className="card p-12 text-center text-[var(--ink-soft)]">
          <p className="text-sm">No applications yet. Browse open roles to get started.</p>
        </div>
      ) : (
        /* Single elevated card with list-row pattern (§1.4) */
        <div className="card overflow-hidden">
          {apps.map((app) => {
            const rawStatus = app.pipelineStage || app.status;
            const { label: statusLabel, chipClass } = getApplicationStatusChip(rawStatus);
            const initials = getInitials(app.orgName);
            const isExpanded = expandedId === app.id;

            return (
              <div key={app.id}>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : app.id)}
                  className="list-row cursor-pointer flex items-center justify-between"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {/* Company initials avatar */}
                    <div className="w-10 h-10 rounded-[10px] bg-[var(--navy-900)] text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-sm">
                      {initials}
                    </div>

                    {/* Role and Org Info */}
                    <div className="min-w-0">
                      <h3
                        className="text-sm font-semibold text-[var(--navy-950)] truncate"
                        style={{ fontFamily: 'var(--font-heading)' }}
                      >
                        {app.jobTitle || 'Engineering Role'}
                      </h3>
                      <p className="text-xs text-[var(--ink-soft)] mt-0.5 truncate">
                        {app.orgName ? `${app.orgName} · ` : ''}Applied {formatDate(app.createdAt)}
                      </p>
                    </div>
                  </div>

                  {/* Right side: Score + Status chip + Chevron */}
                  <div className="flex items-center gap-4 shrink-0 pl-2">
                    {app.interviewScore != null && (
                      <span className="font-tabular text-xs font-semibold text-[var(--ink-soft)]">
                        {Math.round(app.interviewScore)}
                        <span className="text-[11px] font-normal text-[var(--ink-faint)]">/100</span>
                      </span>
                    )}

                    <span className={`chip ${chipClass}`}>
                      {statusLabel}
                    </span>

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`w-4 h-4 text-[var(--ink-faint)] transition-transform duration-150 ${
                        isExpanded ? 'rotate-90 text-[var(--teal-600)]' : ''
                      }`}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </div>

                {/* Expanded Feedback / Details */}
                {isExpanded && (
                  <div className="px-6 py-4 bg-[var(--teal-050)] border-b border-[var(--line-soft)] text-xs space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[var(--navy-950)]">
                        Application Details & Feedback
                      </span>
                      {app.feedback?.overallVerdict && (
                        <VerdictBadge verdict={app.feedback.overallVerdict} />
                      )}
                    </div>

                    {app.feedback?.feedbackText ? (
                      <div className="p-3 rounded-[10px] bg-white border border-[var(--line-soft)]">
                        <FeedbackBody feedback={app.feedback} />
                      </div>
                    ) : (
                      <p className="text-[var(--ink-faint)]">
                        Feedback will be available once the interview evaluation is finalized.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
